import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthProvider, useAuth } from './lib/AuthContext';
import LoginPage from './pages/LoginPage';

// Perf: nur LoginPage (und die AuthProvider/Routing-Hülle oben) laden eager -- alles hinter dem
// Login ist Code-Split, sonst müsste ein unangemeldeter Aufruf von /login den kompletten
// App-Bundle (Dashboard-Charts, SEPA-Export, alle vier Handbuch-Seiten, ...) erst laden, bevor
// überhaupt das Login-Formular erscheint. Owner-Feedback: der Login-Screen lud unverhältnismäßig
// lange.
const AppShell = lazy(() => import('./layout/AppShell'));
const StundenPage = lazy(() => import('./pages/StundenPage'));
const FreigabePage = lazy(() => import('./pages/FreigabePage'));
const AuszahlungPage = lazy(() => import('./pages/AuszahlungPage'));
const SepaAuszahlungPage = lazy(() => import('./pages/SepaAuszahlungPage'));
const AuszahlungsBelegPage = lazy(() => import('./pages/AuszahlungsBelegPage'));
const VerguetungPage = lazy(() => import('./pages/VerguetungPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const KursePage = lazy(() => import('./pages/KursePage'));
const AbteilungenPage = lazy(() => import('./pages/AbteilungenPage'));
const BenutzerPage = lazy(() => import('./pages/BenutzerPage'));
const VereinskontoPage = lazy(() => import('./pages/VereinskontoPage'));
const PostfachPage = lazy(() => import('./pages/PostfachPage'));
const ProfilPage = lazy(() => import('./pages/ProfilPage'));
const HandbuchIndexPage = lazy(() => import('./pages/handbuch/HandbuchIndexPage'));
const HandbuchUebungsleiterPage = lazy(() => import('./pages/handbuch/HandbuchUebungsleiterPage'));
const HandbuchAbteilungsleiterPage = lazy(
  () => import('./pages/handbuch/HandbuchAbteilungsleiterPage'),
);
const HandbuchAdminPage = lazy(() => import('./pages/handbuch/HandbuchAdminPage'));

function RouteFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );
}

export function RequireAuth({ children }: { children: React.ReactElement }) {
  const { loading, idToken } = useAuth();
  const location = useLocation();
  if (loading) return null;
  // Das Ziel mitgeben: Ein Link aus einer Mail (z. B. /auszahlung aus der Sammelmail, ADR-0044)
  // soll nach der Anmeldung dort ankommen, nicht auf der Startseite.
  if (!idToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route path="/" element={<Navigate to="/meine-stunden" replace />} />
            <Route path="/meine-stunden" element={<StundenPage />} />
            <Route path="/freigabe" element={<FreigabePage />} />
            <Route path="/auszahlung" element={<AuszahlungPage />} />
            <Route path="/auszahlung/sepa" element={<SepaAuszahlungPage />} />
            <Route path="/auszahlung/beleg/:runKey" element={<AuszahlungsBelegPage />} />
            <Route path="/verguetung" element={<VerguetungPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/kurse" element={<KursePage />} />
            <Route path="/abteilungen" element={<AbteilungenPage />} />
            <Route path="/benutzer" element={<BenutzerPage />} />
            <Route path="/vereinskonto" element={<VereinskontoPage />} />
            <Route path="/postfach" element={<PostfachPage />} />
            <Route path="/profil" element={<ProfilPage />} />
            <Route path="/handbuch" element={<HandbuchIndexPage />} />
            <Route path="/handbuch/uebungsleiter" element={<HandbuchUebungsleiterPage />} />
            <Route path="/handbuch/abteilungsleiter" element={<HandbuchAbteilungsleiterPage />} />
            <Route path="/handbuch/admin" element={<HandbuchAdminPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
